import { EvidenceGateExperiment, EvidenceGatePeriod, EvidenceGateWeeklyPoint } from "@/http/api-types";

// Timestamps arrive as RFC3339; the calendar date prefix is enough to place an
// experiment in a period, and YYYY-MM-DD compares correctly as a string.
function toDate(timestamp: string): string {
  return timestamp.slice(0, 10);
}

export function experimentsInPeriod(
  experiments: EvidenceGateExperiment[],
  period: EvidenceGatePeriod,
): EvidenceGateExperiment[] {
  const from = toDate(period.from);
  const to = toDate(period.to);
  return experiments.filter(experiment => {
    const concluded = toDate(experiment.concluded_at);
    return concluded >= from && concluded <= to;
  });
}

// paceRatio compares an achieved weekly rate to the rate the gate requires. Null
// means there is no target to compare against yet.
export function paceRatio(ratePerWeek: number, targetPerWeek: number): number | null {
  if (targetPerWeek <= 0) {
    return null;
  }
  return ratePerWeek / targetPerWeek;
}

// byConcludedDesc puts the most recently concluded experiment first. Everything on
// this page is concluded, so the conclusion date is the meaningful recency.
export function byConcludedDesc(experiments: EvidenceGateExperiment[]): EvidenceGateExperiment[] {
  return [...experiments].sort(
    (left, right) => right.concluded_at.localeCompare(left.concluded_at) || right.id - left.id,
  );
}

export type PaceTone = "behind" | "short" | "onTrack" | "none";

export function paceTone(ratio: number | null): PaceTone {
  if (ratio === null) return "none";
  if (ratio < 0.75) return "behind";
  if (ratio < 1) return "short";
  return "onTrack";
}

const PACE_TONE_CLASS: Record<PaceTone, string> = {
  behind: "text-red-600 dark:text-red-400",
  short: "text-orange-600 dark:text-orange-400",
  onTrack: "text-green-600 dark:text-green-400",
  none: "text-muted-foreground",
};

export function paceToneClass(ratio: number | null): string {
  return PACE_TONE_CLASS[paceTone(ratio)];
}

export type VerdictCount = { verdict: string; count: number };

// verdictBreakdown counts why experiments were excluded, most common first, so the
// binding constraint is visible without reading the whole table.
export function verdictBreakdown(experiments: EvidenceGateExperiment[]): VerdictCount[] {
  const counts = new Map<string, number>();
  for (const experiment of experiments) {
    counts.set(experiment.verdict, (counts.get(experiment.verdict) ?? 0) + 1);
  }
  return Array.from(counts, ([verdict, count]) => ({ verdict, count })).sort(
    (left, right) => right.count - left.count || left.verdict.localeCompare(right.verdict),
  );
}

export const MOVING_AVERAGE_WINDOWS = [2, 4, 8] as const;

export type MovingAverageWindow = (typeof MOVING_AVERAGE_WINDOWS)[number];

export type ChartRow = EvidenceGateWeeklyPoint & Record<string, string | number | null>;

// trustedAverageKey / rawAverageKey name the two trailing means of a window, so the
// chart config and the series agree on one spelling.
export function trustedAverageKey(window: number): string {
  return `avg${window}`;
}

export function rawAverageKey(window: number): string {
  return `rawAvg${window}`;
}

// buildChartRows prepares the weekly series for the chart. `excluded` is the part of
// the bar stacked above trusted, so the two segments sum to everything concluded.
// Each average is a trailing mean that stays null until its window has enough weeks
// to fill, so an early partial average never reads as a real trend.
export function buildChartRows(
  weekly: EvidenceGateWeeklyPoint[],
  windows: readonly number[] = MOVING_AVERAGE_WINDOWS,
): ChartRow[] {
  return weekly.map((point, index) => {
    const row: ChartRow = { ...point, excluded: point.raw - point.trusted };
    for (const window of windows) {
      if (index + 1 < window) {
        row[trustedAverageKey(window)] = null;
        row[rawAverageKey(window)] = null;
        continue;
      }
      const slice = weekly.slice(index + 1 - window, index + 1);
      row[trustedAverageKey(window)] = slice.reduce((sum, entry) => sum + entry.trusted, 0) / window;
      row[rawAverageKey(window)] = slice.reduce((sum, entry) => sum + entry.raw, 0) / window;
    }
    return row;
  });
}

export const STACK_KEYS = ["trusted", "excluded"] as const;

// stackTotal adds up only the two stacked bar segments. The tooltip payload also
// carries the trend lines, and those must not be counted into the weekly total.
export function stackTotal(entries: Array<{ dataKey?: string | number; value?: unknown }>): number {
  return entries
    .filter(entry => STACK_KEYS.includes(entry.dataKey as (typeof STACK_KEYS)[number]))
    .reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
}

export function formatRate(value: number): string {
  return value.toFixed(2);
}

export function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}
